import React from 'react';
import { useRouter } from 'next/router';

import { GiftPlan, GiftPlanInterval, useCheckoutNewGiftMutation, useViewerQuery } from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { useValidationErrors } from '@edx/hooks/use-validation-errors';
import { useTrackedCampaigns } from '@edx/hooks/use-track-campaigns';
import { useNotification } from '@edx/hooks/use-notification';
import { Button } from '@edx/components/button';
import { TextInput } from '@edx/components/form';
import { Body, Title } from '@edx/components/typography';
import { ProcessingModal } from '@edx/components/modal';

import { SubscriptionPlans } from './components/subscription-plans';

export const SubscriptionGiftForm: React.FC = () => {
  const router = useRouter();
  const { projectShortName } = useAppConfigStateContext();
  const campaign = useTrackedCampaigns(`${projectShortName}::campaign`);
  const giftFormRef = React.useRef<HTMLDivElement>(null);
  const { showNotification: showErrorNotification } = useNotification('error');
  const { data } = useViewerQuery({ ssr: false });
  const [selectedPlan, setSelectedPlan] = React.useState<GiftPlan>();
  const [email, setEmail] = React.useState('');
  const [showProcessingModal, setShowProcessingModal] = React.useState(false);

  const [onCheckoutNewGift, { loading: isSubmitting, error }] = useCheckoutNewGiftMutation();
  const { errors: validationErrors } = useValidationErrors(error?.graphQLErrors);

  React.useEffect(() => {
    if (data?.viewer.email && !email) setEmail(data.viewer.email);
  }, [data?.viewer.email, email, setEmail]);

  const handleInput = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => setter(event.target.value);

  const handleOpenModal = (): void => {
    if (!selectedPlan?.interval) return;

    window.Chargebee.getInstance().openCheckout({
      hostedPage: async () => {
        const { data } = await onCheckoutNewGift({
          variables: { input: { email, interval: selectedPlan.interval.toLowerCase(), campaign } },
        });

        return data?.checkoutNewGift.hostedPage;
      },
      success: () => {
        setShowProcessingModal(true);
        window.Chargebee.getInstance().closeAll();

        router.push(`/regala-edb/gracias`);
      },
      error: () => {
        showErrorNotification({
          title: '¡Ups!',
          message: 'Se ha producido un error inesperado. Por favor, inténtalo otra vez o contacta con nosotros.',
        });
      },
    });
  };

  const handlePlanSelected = (plan: GiftPlan): void => {
    setSelectedPlan(plan);
    setTimeout(() => {
      giftFormRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <section className="w-full flex flex-col items-center space-y-4 mb-16">
        <SubscriptionPlans selectedPlan={selectedPlan} onSelectPlan={handlePlanSelected} />

        <div className="w-full" ref={giftFormRef}>
          {selectedPlan && (
            <>
              <div className="sticky top-0 z-50 bg-gray-800 text-gray-300 font-body flex justify-between leading-tight">
                <div className="p-6">
                  <Body className="text-gray-500">Tu regalo:</Body>
                  <Title size="large">{selectedPlan.name}</Title>
                  <Body size="small" className="text-gray-600">
                    {selectedPlan.interval === GiftPlanInterval.Quarterly
                      ? 'Suscripción de 3 meses'
                      : 'Suscripción de 1 año'}
                  </Body>
                </div>

                <div className="p-6 flex flex-col justify-end items-end border-l border-gray-700">
                  <Title>{selectedPlan.priceFormatted}</Title>
                  <Body size="small" className="text-gray-600">
                    pago único
                  </Body>
                </div>
              </div>

              <div className="bg-white font-body p-6">
                <div className="text-gray-600 pb-6" onSubmit={handleOpenModal}>
                  <TextInput
                    variant="light"
                    className="mt-1 font-medium"
                    label="Introduce tu e-mail"
                    labelClassName="col-span-12 flex flex-col"
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    error={validationErrors.email}
                    onChange={handleInput(setEmail)}
                    required
                  />

                  <Body tag="span" size="small">
                    Recibirás un e-mail de confirmación una vez finalizada la compra
                  </Body>
                </div>

                <Button
                  variant="primary"
                  size="xlarge"
                  textVariant="medium"
                  disabled={isSubmitting}
                  onClick={handleOpenModal}
                >
                  Continuar
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {showProcessingModal && <ProcessingModal />}
    </>
  );
};
